/**
 * CounterSignDialog Component
 * Dialog for DJ to counter-sign a contract after client has signed
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Edit, Check } from 'lucide-react';

export default function CounterSignDialog({
  open,
  onOpenChange,
  contract,
  signerName,
  onSignerNameChange,
  onConfirm,
  signing = false
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-card border-glass-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5 text-orange-500" />
            Counter-Sign Contract
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/30">
            <p className="text-sm">
              <span className="font-medium text-orange-500">Client has signed this contract.</span>{' '}
              By counter-signing, you finalize the agreement.
            </p>
          </div>

          {/* Client's Signature Info */}
          {contract?.client_signed_at && (
            <div className="p-4 rounded-lg bg-muted/30 border border-border">
              <p className="text-xs text-muted-foreground mb-1">Client Signature:</p>
              <p className="text-xl italic" style={{ fontFamily: "'Dancing Script', cursive" }}>
                {contract.client_signer_name || contract.client_signature}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Signed: {new Date(contract.client_signed_at).toLocaleString()}
              </p>
            </div>
          )}

          <div>
            <Label>Type your full name to counter-sign</Label>
            <Input
              value={signerName}
              onChange={(e) => onSignerNameChange(e.target.value)}
              placeholder="Your full name"
              className="mt-2 text-lg bg-muted/20 border-glass-border"
              data-testid="counter-signer-name"
            />
          </div>

          {/* Signature Preview */}
          {signerName && (
            <div className="p-4 rounded-lg border-2 border-dashed border-neon-purple">
              <p className="text-xs text-muted-foreground mb-2">Your Signature:</p>
              <p 
                className="text-3xl italic text-center py-4 text-neon-purple" 
                style={{ fontFamily: "'Dancing Script', cursive" }}
              >
                {signerName}
              </p>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            This electronic signature will be legally binding. Your IP address and timestamp will be recorded.
          </p>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="border-glass-border">
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              disabled={signing || !signerName?.trim()}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="confirm-counter-sign-btn"
            >
              {signing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Signing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Counter-Sign
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
